import {
    LocalStorageKey,
    PopoverEvent,
    HtmlAttr,
    SlidingDrawerEvent
} from 'constants/enums'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { attachEventHandlerWithFemFlagCheck } from 'utils/events'
import { CustomObjectApiService } from './../user-custom-objects/api-service'
import { SavedArticlesStore, SavedArticlesData } from './store'
import { saveArticleEvent, clickCtaEvent } from './analytics'
import { logger } from 'utils/system/logger'

export const iconClose = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.89 8.7L12 10.59L10.11 8.7C9.72 8.31 9.09 8.31 8.7 8.7C8.31 9.09 8.31 9.72 8.7 10.11L10.59 12L8.7 13.89C8.31 14.28 8.31 14.91 8.7 15.3C9.09 15.69 9.72 15.69 10.11 15.3L12 13.41L13.89 15.3C14.28 15.69 14.91 15.69 15.3 15.3C15.69 14.91 15.69 14.28 15.3 13.89L13.41 12L15.3 10.11C15.69 9.72 15.69 9.09 15.3 8.7C14.91 8.32 14.27 8.32 13.89 8.7ZM12 2C6.47 2 2 6.47 2 12C2 17.53 6.47 22 12 22C17.53 22 22 17.53 22 12C22 6.47 17.53 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="#950000"/>
</svg>
`

export const errorMessageHtml = `
Oops! Something isn't working. Try again later.
<button
    aria-label="remove error message"
    onclick="location.reload()"
    type="button">
    ${iconClose}
</button>
`

export const popoverMessageHtml = `
<p>You can save articles to your account! Find saved stories in your <a class="bookmark-link" href="/saved-articles/" data-evt-skip-click="true">account</a>.</p>
`

const SUBSCRIBER_MAX_LIMIT = '100'
const REGISTER_MAX_LIMIT = '20'

export class SavedArticlesComponent extends ComponentBase {
    private customObjectApiService: CustomObjectApiService
    private lrtKey: string | undefined
    private enableTooltip: boolean
    private bookmarkWrpper: HTMLDivElement
    private bookmarkButton: HTMLButtonElement

    public constructor(protected bookmarks: HTMLDivElement) {
        super()

        this.customObjectApiService = new CustomObjectApiService()
        this.lrtKey = undefined

        this.handleCtaEvent()
        attachEventHandlerWithFemFlagCheck('pnIdReady', () => {
            this.lrtKey = this.lrToken()
            if (this.lrtKey) {
                void this.initSaveArticleButton()
            }
        })
    }

    private lrToken(): string | undefined {
        const token = localStorage.getItem(LocalStorageKey.LRTOKENKEY)
        return token && token != '' ? token : undefined
    }

    private handleCtaEvent(): void {
        const ctaLink = this.bookmarks.querySelectorAll('.bookmark-link')
        ctaLink.forEach((link: HTMLAnchorElement) => {
            link.addEventListener('click', () => {
                sessionStorage.setItem('redirect_url', window.location.href)
                clickCtaEvent(link)
            })
        })
    }

    /**
     * Updates the article card click analytic configuration.
     * @param state The new state of the article card.
     * @param targetSelector CSS selector to find the element within the optional parent.
     * @param parentElement The container element to search within, defaults to `document`.
     * @returns The updated HTMLElement or undefined if not found or error occurs.
     */
    public updateArticleCardClickAnalyticConfig(
        state: boolean,
        targetElement: HTMLElement,
        parentElementSelector: string
    ): HTMLElement | undefined {
        const element: HTMLElement | null = targetElement?.closest(
            parentElementSelector
        )

        if (!element) {
            logger('info', 'Element not found. Skipping evt-val update.')
            return undefined
        }

        const elementDataEventVal = element.dataset?.evtVal
        if (!elementDataEventVal) {
            logger(
                'info',
                'Data event value is missing from the element. Skipping update.'
            )
            return undefined
        }

        try {
            const eventValJsonObject = JSON.parse(elementDataEventVal)
            eventValJsonObject.bookmarked = String(state)
            eventValJsonObject.control_fields.mparticle.keys.bookmarked =
                'bookmarked'
            element.dataset.evtVal = JSON.stringify(eventValJsonObject)
            // data-evt-val-changed="true" is used to indicate that the data-evt-val has been updated
            // so the fem event handler can re-parse the data-evt-val attribute
            element.dataset.evtValChanged = 'true'
            return element
        } catch (error) {
            logger('error', 'Failed to parse or update event data:', error)
            return undefined
        }
    }

    public async initSaveArticleButton(): Promise<void> {
        await this.getCustomObjects()
        const saveArticleBookmarks =
            this.bookmarks.querySelectorAll('.bookmark-button')
        const savedArticles = SavedArticlesStore.getInstance().savedArticles()
        this.enableTooltip = false

        // get all bookmark elements and get data-article-id
        // and if it's found in savedArticles array then change the button text to 'Saved'
        saveArticleBookmarks.forEach((button: HTMLButtonElement) => {
            // change popover action from click event to custom event to avoid showing popover before get response
            if (button && button.classList.contains('popover__trigger')) {
                button.classList.add('popover__event')
            }

            const popover = button.closest('.popover') as HTMLDivElement
            const popoverContent = popover.querySelector(
                '.bookmark__popover__content'
            )
            const bookmark = button.querySelector(
                '.save-article-button'
            ) as HTMLElement

            if (popoverContent) {
                popoverContent.innerHTML = popoverMessageHtml
            }

            const articleId = bookmark.getAttribute('data-article-id')

            if (articleId) {
                // check first bookmark
                if (
                    SavedArticlesStore.getInstance().savedArticlesNumber() < 1
                ) {
                    this.enableTooltip = true
                }

                const article = savedArticles[articleId]
                if (article) {
                    this.setBookmarkState(bookmark, true)
                    this.updateArticleCardClickAnalyticConfig(
                        true,
                        bookmark,
                        '.article-card'
                    )
                } else {
                    this.setBookmarkState(bookmark, false)
                    this.updateArticleCardClickAnalyticConfig(
                        false,
                        bookmark,
                        '.article-card'
                    )
                }

                button.onclick = () => {
                    if (!this.lrtKey) {
                        return
                    }
                    // make a button disable to prevent multiple click before API response
                    // the button will be clickable after success API response
                    button.disabled = true
                    this.bookmarkButton = button
                    this.bookmarkWrpper = button.closest(
                        '.bookmark-wrapper'
                    ) as HTMLDivElement
                    const objectId = SavedArticlesStore.getInstance().objectId()
                    const articleState =
                        bookmark.getAttribute('data-article-state')

                    if (articleState !== 'saved') {
                        this.enableFocusMaxLimitDrawer()
                        if (
                            SavedArticlesStore.getInstance().savedArticlesNumber() >=
                            this.getMaxLimit()
                        ) {
                            window.dispatchEvent(
                                new CustomEvent(SlidingDrawerEvent.OPEN, {
                                    detail: 'slidingDrawersaveArticleDrawer'
                                })
                            )
                            this.bookmarkButton.disabled = false
                            saveArticleEvent(articleId, 'saved_limit_reached')
                        } else {
                            this.saveArticle(bookmark, articleId, objectId)
                        }
                        this.updateArticleCardClickAnalyticConfig(
                            true,
                            bookmark,
                            '.article-card'
                        )
                    } else {
                        this.removeSavedArticle(bookmark, articleId, objectId)
                        this.updateArticleCardClickAnalyticConfig(
                            false,
                            bookmark,
                            '.article-card'
                        )
                    }
                }
            }
        })
    }

    /**
     * Get users saved articles from CustomObject.
     */
    private async getCustomObjects(): Promise<void> {
        if (!this.lrtKey) {
            logger('error', 'LRT key is not found')
            return
        }
        await this.customObjectApiService
            .getCustomObjects(this.lrtKey)
            .then((response) => {
                const savedArticleData = response.data as SavedArticlesData
                if (Object.keys(savedArticleData).includes('SavedArticles')) {
                    SavedArticlesStore.getInstance().update(savedArticleData)
                }
            })
            .catch((error) => {
                logger('error', error)
            })
    }

    /**
     * Save an article to custom object data in logingRadius and set bookmark state
     * @param bookmark - bookmark HTML element
     * @param articleId - article id
     * @param objectId - object ID
     */
    private saveArticle(
        bookmark: HTMLElement,
        articleId: string,
        objectId: string
    ): void {
        this.customObjectApiService
            .saveArticle(this.lrtKey, articleId, objectId)
            .then((response) => {
                if (response.data) {
                    SavedArticlesStore.getInstance().add(
                        response.data.article_id,
                        response.data.object_id,
                        response.data.object_modified_on,
                        response.data.saved_on
                    )
                }
                if (this.enableTooltip) {
                    bookmark
                        .closest('.popover')
                        ?.dispatchEvent(new CustomEvent(PopoverEvent.OPEN))
                    this.enableTooltip = false
                    this.handleCtaEvent()
                }
                this.setBookmarkState(bookmark, true)
                this.bookmarkButton.disabled = false
                saveArticleEvent(articleId, 'saved')
            })
            .catch((error) => {
                logger('error', error)
                this.handleErrorMessage()
                saveArticleEvent(articleId, 'failed')
            })
    }

    /**
     * Remove an article from custom object data in logingRadius and set bookmark state
     * @param bookmark - bookmark HTML element
     * @param articleId - article id
     * @param objectId - object ID
     */
    private removeSavedArticle(
        bookmark: HTMLElement,
        articleId: string,
        objectId: string
    ): void {
        const modifiedOn = SavedArticlesStore.getInstance().modifiedDate()
        this.customObjectApiService
            .removeSavedArticle(this.lrtKey, articleId, objectId, modifiedOn)
            .then((result) => {
                if (result.data) {
                    SavedArticlesStore.getInstance().delete(
                        result.data.article_id,
                        result.data.object_modified_on
                    )
                }
                if (
                    SavedArticlesStore.getInstance().savedArticlesNumber() < 1
                ) {
                    this.enableTooltip = true
                }
                this.setBookmarkState(bookmark, false)
                this.bookmarkButton.disabled = false
                saveArticleEvent(articleId, 'unsaved')
            })
            .catch((error) => {
                logger('error', error)
                this.handleErrorMessage()
                saveArticleEvent(articleId, 'failed')
            })
    }

    private handleErrorMessage(): void {
        const parentEl = this.bookmarkWrpper.parentElement
        const articleCardEl = this.bookmarkWrpper.closest('.article-card')
        const divContainer = document.createElement('div')
        divContainer.classList.add(
            'message-box--error',
            'flex-align-center',
            'flex-justify-between'
        )
        divContainer.innerHTML = errorMessageHtml
        if (articleCardEl) {
            articleCardEl?.appendChild(divContainer)
        } else {
            parentEl?.appendChild(divContainer)
        }
        this.bookmarkWrpper.classList.add('hidden')
    }

    /**
     * Set bookmark state
     * @param bookmark bookmark HTML element
     * @param state true for saved bookmark, false for unsaved bookmark
     */
    private setBookmarkState(bookmark: HTMLElement, state: boolean): void {
        if (state) {
            bookmark.classList.add('save-article-button--active')
            bookmark.classList.remove('save-article-button--default')
            bookmark.setAttribute('data-article-state', 'saved')
            bookmark.setAttribute('title', 'Remove from saved articles')
        } else {
            bookmark.classList.remove('save-article-button--active')
            bookmark.classList.add('save-article-button--default')
            bookmark.setAttribute('data-article-state', 'unsaved')
            bookmark.setAttribute('title', 'Save article')
        }
    }

    private enableFocusMaxLimitDrawer(): void {
        const drawerSection = this.getElement('.save-article__drawer')

        if (!drawerSection) {
            return
        }

        const focusableElements = [
            ...drawerSection.querySelectorAll('a[href], button, input')
        ]
        focusableElements.map((el) => el.setAttribute(HtmlAttr.TAB_INDEX, '0'))
        drawerSection.focus()
        drawerSection.setAttribute('aria-hidden', 'true')
    }

    private getMaxLimit(): number {
        const userType = this.getUserType()
        const drawerEl = this.getElement('.save-article__drawer-message')

        const subMaxLimit =
            drawerEl?.getAttribute('data-sub-max-limit') || SUBSCRIBER_MAX_LIMIT
        const regMaxLimit =
            drawerEl?.getAttribute('data-reg-max-limit') || REGISTER_MAX_LIMIT

        if (userType === 'register') {
            return parseInt(regMaxLimit)
        }
        return parseInt(subMaxLimit)
    }

    private getUserType(): string {
        const userData = JSON.parse(
            localStorage.getItem(LocalStorageKey.XID) || '{}'
        ).data

        if (userData && userData.adLight) {
            return 'subscriber'
        }
        return 'register'
    }
}
